import { UserRole } from 'types';

/**
 * ERP-110 — «Исправить группу (ошибка ввода)» и «Отменить зачисление».
 *
 * Правило «можно ли» живёт на сервере (`agreementCorrectionCheck` и мутации
 * считают блокеры одной функцией). Здесь только то, что нужно, чтобы его
 * НАРИСОВАТЬ: кому показывать пункты меню, как перевести код и какие коды
 * относятся к выбранной группе.
 */

/**
 * Кому показывать «Исправить группу» — те же роли, что у перевода
 * (`changeAgreementGroup`) и что пускает бэкенд (`CORRECT_AGREEMENT_ROLES`).
 */
export const AGREEMENT_CORRECT_ROLES: readonly UserRole[] = [
  UserRole.ADMIN,
  UserRole.ACCOUNTANT,
  UserRole.SALESMANAGER,
  UserRole.FINANCIER,
];

/**
 * Кому показывать «Отменить зачисление» — без продажника: отмена удаляет
 * договор. Зеркало `UNDO_ENROLLMENT_ROLES`; если список разойдётся, сервер всё
 * равно откажет (`ROLE_NOT_ALLOWED` приходит и в предпроверке).
 */
export const ENROLLMENT_UNDO_ROLES: readonly UserRole[] = [
  UserRole.ADMIN,
  UserRole.FINANCIER,
  UserRole.ACCOUNTANT,
];

export const hasAnyCorrectionRole = (
  roles: readonly UserRole[],
  allowed: readonly UserRole[],
): boolean => roles.some((role) => allowed.includes(role));

export const CORRECTION_NS = 'pages.agreementCorrection';

/**
 * Коды отказа, которые умеет назвать экран. Порядок — как отдаёт сервер
 * (`BLOCKER_ORDER`), плюс коды разбора аргументов мутаций.
 */
export const CORRECTION_BLOCKER_CODES = [
  'TERMINATED',
  'TRANSFER',
  'WINDOW_PASSED',
  'HAS_INVOICES',
  'ISSUE_IN_PROGRESS',
  'HAS_MONEY',
  'HAS_JOURNAL',
  'SIGNED_DOCUMENT',
  'LINKED',
  'OTHER_AGREEMENT_IN_GROUP',
  'SAME_GROUP',
  'TARGET_OTHER_ENTITY',
  'TARGET_NO_PRICE',
  'TARGET_FINISHED',
  'ALREADY_IN_GROUP',
  'TARGET_NOT_FOUND',
  'ROLE_NOT_ALLOWED',
  'AGREEMENT_NOT_FOUND',
  'REASON_REQUIRED',
] as const;

/** Предупреждения — не мешают, но о них надо сказать до действия. */
export const CORRECTION_WARNING_CODES = [
  'SURVEY_RESPONSES',
  'PANDADOC_DOCUMENT',
  'PARTIAL_ATTENDANCE_RESET',
  'LAST_AGREEMENT',
  'TARGET_PRICE_DIFFERS',
] as const;

const KNOWN_BLOCKERS: ReadonlySet<string> = new Set(CORRECTION_BLOCKER_CODES);
const KNOWN_WARNINGS: ReadonlySet<string> = new Set(CORRECTION_WARNING_CODES);

/**
 * Коды про ВЫБРАННУЮ группу (серверный `TARGET_BLOCKERS` + `TARGET_NOT_FOUND`)
 * — их показывают у поля группы, остальные — над формой.
 *
 * `SIGNED_DOCUMENT` здесь тоже: в проверке С выбранной группой он значит
 * «договор подписан на другую группу/курс» — это отказ именно этой группе,
 * другую выбрать можно. В базовой проверке (без группы) он приходит, только
 * если подходящей группы нет вообще, — и тогда форма не показывается.
 */
export const TARGET_BLOCKER_CODES: ReadonlySet<string> = new Set([
  'SAME_GROUP',
  'TARGET_OTHER_ENTITY',
  'TARGET_NO_PRICE',
  'TARGET_FINISHED',
  'ALREADY_IN_GROUP',
  'TARGET_NOT_FOUND',
  'SIGNED_DOCUMENT',
]);

/**
 * Ключ перевода кода отказа. Незнакомый код (сервер завёл новый, фронт ещё
 * нет) — общая фраза с кодом, а не сырой ключ на экране.
 */
export const blockerKey = (code: string): string =>
  KNOWN_BLOCKERS.has(code)
    ? `${CORRECTION_NS}.blockers.${code}`
    : `${CORRECTION_NS}.blockers.unknown`;

/**
 * Коды, текст которых зависит от операции: подписанный договор мешает
 * правке, только если он на другую группу/курс, а отмене — всегда.
 */
const MODE_SPECIFIC_BLOCKERS: ReadonlySet<string> = new Set([
  'SIGNED_DOCUMENT',
]);

/**
 * Ключ и подстановки текста отказа. Срок отмены (`WINDOW_PASSED`) называется
 * датой, если она известна (`undoDeadline`, уже отформатированная), — без
 * зашитого числа дней: окно настраивается на сервере.
 */
export const blockerI18n = (
  code: string,
  mode: 'correct' | 'undo',
  context: { deadline?: string | null } = {},
): { key: string; vars: Record<string, string> } => {
  if (code === 'WINDOW_PASSED' && context.deadline) {
    return {
      key: `${CORRECTION_NS}.blockers.WINDOW_PASSED_AT`,
      vars: { date: context.deadline },
    };
  }
  if (MODE_SPECIFIC_BLOCKERS.has(code)) {
    return {
      key: `${CORRECTION_NS}.blockers.${code}_${mode.toUpperCase()}`,
      vars: {},
    };
  }
  return { key: blockerKey(code), vars: { code } };
};

/** Отказ, который лечится ожиданием: предложить «Проверить ещё раз». */
export const hasRetryableBlocker = (codes: readonly string[]): boolean =>
  codes.includes('ISSUE_IN_PROGRESS');

/** То же для предупреждений. */
export const warningKey = (code: string): string =>
  KNOWN_WARNINGS.has(code)
    ? `${CORRECTION_NS}.warnings.${code}`
    : `${CORRECTION_NS}.warnings.unknown`;

export type CorrectionMode = 'correct' | 'undo';

const CORRECT_ONLY_WARNINGS: ReadonlySet<string> = new Set([
  'PANDADOC_DOCUMENT',
  'PARTIAL_ATTENDANCE_RESET',
  'TARGET_PRICE_DIFFERS',
]);

/**
 * Предупреждения правки, которые после «Исправить» становятся делом
 * оператора, — повторяются в чек-листе экрана «готово».
 */
export const DONE_CHECKLIST_WARNINGS: readonly string[] = [
  'TARGET_PRICE_DIFFERS',
  'PARTIAL_ATTENDANCE_RESET',
  'PANDADOC_DOCUMENT',
];

/** Почему номер договора не пересчитан (`titleKeptReason`). */
export const TITLE_KEPT_REASONS = [
  'SIGNED_DOCUMENT',
  'MANUAL_TITLE',
  'NAME_TOO_LONG',
] as const;
const KNOWN_TITLE_KEPT: ReadonlySet<string> = new Set(TITLE_KEPT_REASONS);

export const titleKeptKey = (reason: string): string =>
  KNOWN_TITLE_KEPT.has(reason)
    ? `${CORRECTION_NS}.titleKept.${reason}`
    : `${CORRECTION_NS}.titleKept.unknown`;
const UNDO_ONLY_WARNINGS: ReadonlySet<string> = new Set(['LAST_AGREEMENT']);

/**
 * Сервер отдаёт предупреждения обеих операций одним списком. «Других договоров
 * нет» — про отмену, «сверьте курс PandaDoc» и «другая цена» — про правку.
 * Незнакомый код показываем в обоих окнах: новое предупреждение сервера не
 * должно пропасть молча.
 */
export const warningsFor = (
  mode: CorrectionMode,
  codes: readonly string[],
): string[] =>
  codes.filter((code) =>
    mode === 'correct'
      ? !UNDO_ONLY_WARNINGS.has(code)
      : !CORRECT_ONLY_WARNINGS.has(code),
  );

/**
 * Предупреждения про ВЫБРАННУЮ группу (серверные «цели»). Пока выбранной
 * группе отказано, они шум: под «в эту группу нельзя» список «у группы другая
 * цена» только сбивает — выбрать всё равно придётся другую.
 */
export const TARGET_WARNING_CODES: ReadonlySet<string> = new Set([
  'TARGET_PRICE_DIFFERS',
]);

/** Предупреждения правки к показу: без целевых, если группе отказано. */
export const visibleCorrectWarnings = (
  codes: readonly string[],
  targetBlocked: boolean,
): string[] => {
  const list = warningsFor('correct', codes);
  return targetBlocked
    ? list.filter((code) => !TARGET_WARNING_CODES.has(code))
    : list;
};

/** Разнести отказ правки: про договор (над формой) и про группу (у поля). */
export const splitCorrectBlockers = (
  codes: readonly string[],
): { agreement: string[]; target: string[] } => ({
  agreement: codes.filter((code) => !TARGET_BLOCKER_CODES.has(code)),
  target: codes.filter((code) => TARGET_BLOCKER_CODES.has(code)),
});

/**
 * Предлагать ли перевод вместо правки/отмены. Расторгнутый договор перевести
 * нельзя, отказ по правам переводом не лечится — в остальных случаях перевод и
 * есть штатный путь («по договору уже есть деньги, отметки…»).
 */
export const transferIsAlternative = (codes: readonly string[]): boolean =>
  codes.length > 0 &&
  !codes.includes('TERMINATED') &&
  !codes.includes('AGREEMENT_NOT_FOUND');

/** Все ключи раздела, которые строятся из кодов, — для локального гейта. */
export const CORRECTION_CODE_I18N_KEYS: readonly string[] = [
  ...CORRECTION_BLOCKER_CODES.filter((c) => !MODE_SPECIFIC_BLOCKERS.has(c)).map(
    (c) => `${CORRECTION_NS}.blockers.${c}`,
  ),
  ...Array.from(MODE_SPECIFIC_BLOCKERS).flatMap((c) => [
    `${CORRECTION_NS}.blockers.${c}_CORRECT`,
    `${CORRECTION_NS}.blockers.${c}_UNDO`,
  ]),
  `${CORRECTION_NS}.blockers.WINDOW_PASSED_AT`,
  `${CORRECTION_NS}.blockers.unknown`,
  ...CORRECTION_WARNING_CODES.map((c) => `${CORRECTION_NS}.warnings.${c}`),
  `${CORRECTION_NS}.warnings.unknown`,
  ...TITLE_KEPT_REASONS.map((r) => `${CORRECTION_NS}.titleKept.${r}`),
  `${CORRECTION_NS}.titleKept.unknown`,
];
